import { useState } from "react";
import PageTitle from "../layout/PageTitle";
import Button from "../layout/Button";
import MenuBreadcrumb from "./menu/MenuBreadcrumb";
import MenuOptionCard from "./menu/MenuOptionCard";
import AddMenuOptionCard from "./menu/AddMenuOptionCard";
import MenuOptionModal, { MenuOptionModalResult } from "./menu/MenuOptionModal";
import MenuNodeMessageModal from "./menu/MenuNodeMessageModal";
import { WhatsappBotHook } from "../../hooks/WhatsappBotPageHook";
import { WhatsappBotError, WhatsappBotMenuNode, WhatsappBotMenuOption } from "../../model/whatsappBot.model";
import {
  buildMenuBreadcrumbs,
  emptyMenuNode,
  findMenuNode,
  nextAvailableDigit,
  updateNodeAtPath,
} from "../../model/whatsappBotMenu.utils";

function MenuConfig({ botHook }: MenuConfigProps) {
  const [path, setPath] = useState<number[]>([]);
  const [optionModal, setOptionModal] = useState<{
    mode: "add" | "edit";
    option?: WhatsappBotMenuOption;
  } | null>(null);
  const [messageModalShown, setMessageModalShown] = useState(false);

  const root = botHook.menu || emptyMenuNode();
  const currentNode = findMenuNode(root, path);
  const crumbs = buildMenuBreadcrumbs(root, path);
  const nextDigit = nextAvailableDigit(currentNode.options);

  const mutateMenu = (updater: (node: WhatsappBotMenuNode) => WhatsappBotMenuNode) => {
    botHook.setMenu(updateNodeAtPath(root, path, updater));
    botHook.setHasUnsavedChanges(true);
  };

  const handleSaveOption = (result: MenuOptionModalResult) => {
    let newOption: WhatsappBotMenuOption;

    if (result.kind === "submenu") {
      const editingOption = optionModal?.option;
      const node =
        editingOption?.type === "submenu" ? editingOption.node : emptyMenuNode();
      newOption = {
        option: result.option,
        name: result.name,
        type: "submenu",
        node,
        ...(result.confirmationMessage ? { confirmationMessage: result.confirmationMessage } : {}),
      };
    } else {
      newOption = {
        option: result.option,
        name: result.name,
        type: "queue",
        queueId: result.queueId,
        ...(result.confirmationMessage ? { confirmationMessage: result.confirmationMessage } : {}),
      };
    }

    const editingDigit = optionModal?.option?.option;

    mutateMenu((node) => ({
      ...node,
      options:
        optionModal?.mode === "edit"
          ? node.options.map((o) => (o.option === editingDigit ? newOption : o))
          : [...node.options, newOption],
    }));

    setOptionModal(null);
  };

  const handleDeleteOption = (option: WhatsappBotMenuOption) => {
    mutateMenu((node) => ({
      ...node,
      options: node.options.filter((o) => o.option !== option.option),
    }));
  };

  const handleSaveMessage = (message: string, error: WhatsappBotError | undefined) => {
    mutateMenu((node) => ({ ...node, message, error }));
    setMessageModalShown(false);
  };

  return (
    <>
      <PageTitle>{path.length === 0 ? "Suas Opções" : "Opções deste Nível"}</PageTitle>

      <MenuBreadcrumb crumbs={crumbs} onNavigate={setPath} />

      <div className="bg-white border-2 border-white-dark rounded-15 py-4 px-6 mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-gray-dark text-[11px] font-bold uppercase tracking-wide mb-1">
            Mensagem de saudação deste nível
          </div>
          <div className="text-primary font-medium text-sm">
            {currentNode.message ? "Configurada" : "Ainda não configurada"}
          </div>
        </div>
        <Button outline onClick={() => setMessageModalShown(true)}>
          Editar mensagem
        </Button>
      </div>

      <div className="grid grid-cols-10 gap-4 2xl:grid-cols-ivrCard grid-cols-ivrCard-sm">
        {currentNode.options
          .slice()
          .sort((a, b) => a.option - b.option)
          .map((option) => (
            <MenuOptionCard
              key={option.option}
              option={option}
              queue={
                option.type === "queue"
                  ? botHook.queues.find((q) => q.id === option.queueId)
                  : undefined
              }
              onOpenSubmenu={() => setPath([...path, option.option])}
              onEditOption={() => setOptionModal({ mode: "edit", option })}
              onDelete={() => handleDeleteOption(option)}
              className="w-full h-full hover:ring-expand hover:drop-shadow-far transition-filter ring-white duration-200"
            />
          ))}
        <AddMenuOptionCard
          onCreate={() => setOptionModal({ mode: "add" })}
          disabled={nextDigit === null}
          nextDigit={nextDigit}
        />
      </div>

      <MenuOptionModal
        shown={!!optionModal}
        mode={optionModal?.mode || "add"}
        option={optionModal?.option}
        initialDigit={nextDigit ?? undefined}
        existingOptions={currentNode.options}
        queues={botHook.queues}
        onClose={() => setOptionModal(null)}
        onSave={handleSaveOption}
      />

      <MenuNodeMessageModal
        shown={messageModalShown}
        node={currentNode}
        queues={botHook.queues}
        onClose={() => setMessageModalShown(false)}
        onSave={handleSaveMessage}
      />
    </>
  );
}

export default MenuConfig;

export type MenuConfigProps = {
  botHook: WhatsappBotHook;
};
